/**
 * Permissions Constants
 *
 * Enumerated list of all available permissions in the PerfectServe system.
 * These constants should be used throughout the application when checking permissions
 * to ensure consistency and prevent typos.
 */

// Role-based permissions
export const ROLES = {
  PERFECT_SERVE_EMPLOYEE: "role://PerfectServe Employee",
  PRACTICE_ADMINISTRATOR: "role://Practice Administrator",
  IDENTITY_ADMINISTRATOR: "role://Identity Administrator",
  FACILITY_ADMINISTRATOR: "role://Facility Administrator",
  HOSPITAL_PRACTITIONER_ADMINISTRATOR: "role://Hospital Practitioner Administrator",
  CARE_TEAM_ADMINISTRATOR: "role://Care Team Administrator",
  HOSPITAL_LOGIN_USER_ADMINISTRATOR: "role://Hospital Login User Administrator",
  MIDMARKET_ADMINISTRATOR: "role://Midmarket Administrator",
  HOSPITAL_PRACTITIONER_ACCOUNT_ADMINISTRATOR: "role://Hospital Practitioner Account Administrator",
  COPY_USER_CONFIGURATION_ADMINISTRATOR: "role://Copy User Configuration Administrator",
  REFERENCE_USER_ADMINISTRATOR: "role://Reference User Administrator",
  CALL_RECORDING_ADMINISTRATOR: "role://Call Recording Administrator",
  USER_PROVISIONING_ADMINISTRATOR: "role://User Provisioning Administrator",
  CAREWIRE_ADMINISTRATOR: "role://CareWire Administrator",
  ROSTER_UPLOAD_ADMINISTRATOR: "role://Roster Upload Administrator",
  MIDMARKET_ADMINISTRATOR_NO_ADD_USER: "role://Midmarket Administrator – No Add User",
  MIDMARKET_ADMINISTRATOR_NO_LOGIN_USER: "role://Midmarket Administrator – No Login User",
  CSP_TRACKING: "role://CSP Tracking",
  DEACTIVATE_USER: "role://Deactivate User",
  SHARED_INBOX: "role://Shared Inbox",
} as const;

// Object-based permissions
export const OBJECTS = {
  // Identity permissions
  IDENTITY_CREATE: "obj://Identity/Create",
  IDENTITY_READ: "obj://Identity/Read",
  IDENTITY_UPDATE: "obj://Identity/Update",

  // User Principal Name permissions
  USER_PRINCIPAL_NAME_CREATE: "obj://UserPrincipalName/Create",
  USER_PRINCIPAL_NAME_DELETE: "obj://UserPrincipalName/Delete",
  USER_PRINCIPAL_NAME_READ: "obj://UserPrincipalName/Read",
  USER_PRINCIPAL_NAME_UPDATE: "obj://UserPrincipalName/Update",

  // Authenticator permissions
  AUTHENTICATOR_READ: "obj://Authenticator/Read",
  AUTHENTICATOR_ASSOCIATE: "obj://Authenticator/Associate",
  AUTHENTICATOR_DISASSOCIATE: "obj://Authenticator/Disassociate",

  // User permissions
  USER_CREATE: "obj://User/Create",
  USER_READ: "obj://User/Read",
  USER_UPDATE: "obj://User/Update",

  // Practitioner permissions
  PRACTITIONER_CREATE: "obj://Practitioner/Create",
  PRACTITIONER_READ: "obj://Practitioner/Read",
  PRACTITIONER_UPDATE: "obj://Practitioner/Update",

  // Practice Account permissions
  PRACTICE_ACCOUNT_CREATE: "obj://PracticeAccount/Create",
  PRACTICE_ACCOUNT_READ: "obj://PracticeAccount/Read",
  PRACTICE_ACCOUNT_UPDATE: "obj://PracticeAccount/Update",

  // Facility permissions
  FACILITY_CREATE: "obj://Facility/Create",
  FACILITY_READ: "obj://Facility/Read",
  FACILITY_UPDATE: "obj://Facility/Update",

  // HSO permissions
  HSO_CREATE: "obj://Hso/Create",
  HSO_READ: "obj://Hso/Read",
  HSO_UPDATE: "obj://Hso/Update",

  // PSO permissions
  PSO_CREATE: "obj://Pso/Create",
  PSO_READ: "obj://Pso/Read",
  PSO_UPDATE: "obj://Pso/Update",

  // Directory permissions
  DIRECTORY_CREATE: "obj://Directory/Create",
  DIRECTORY_READ: "obj://Directory/Read",
  DIRECTORY_UPDATE: "obj://Directory/Update",
  DIRECTORY_ASSOCIATE: "obj://Directory/Associate",
  DIRECTORY_DISASSOCIATE: "obj://Directory/Disassociate",

  // Care Team Member permissions
  CARE_TEAM_MEMBER_CREATE: "obj://CareTeamMember/Create",
  CARE_TEAM_MEMBER_READ: "obj://CareTeamMember/Read",
  CARE_TEAM_MEMBER_UPDATE: "obj://CareTeamMember/Update",

  // Hospital Account permissions
  HOSPITAL_ACCOUNT_CREATE: "obj://HospitalAccount/Create",
  HOSPITAL_ACCOUNT_READ: "obj://HospitalAccount/Read",
  HOSPITAL_ACCOUNT_UPDATE: "obj://HospitalAccount/Update",

  // Midmarket Account permissions
  MIDMARKET_ACCOUNT_CREATE: "obj://MidmarketAccount/Create",
  MIDMARKET_ACCOUNT_READ: "obj://MidmarketAccount/Read",
  MIDMARKET_ACCOUNT_UPDATE: "obj://MidmarketAccount/Update",

  // Directory Contact permissions
  DIRECTORY_CONTACT_READ: "obj://DirectoryContact/Read",
} as const;

// Application-based permissions
export const APPLICATIONS = {
  CSP_LOGIN: "app://CSP/Login",
  CSP_AFTER_HOURS_REPORTING: "app://CSP/After Hours Reporting",
  CSP_COPY_CONFIGURATION: "app://CSP/Copy Configuration",
  CSP_USER_DEACTIVATION: "app://CSP/User Deactivation",
  CSP_PRIVILEGE_MANAGEMENT: "app://CSP/Privilege Management",
  CSP_AUTHENTICATOR_MANAGEMENT: "app://CSP/Authenticator Management",
  CSP_COPY_REFERENCE_USER: "app://CSP/Copy Reference User",
  CSP_REFERENCE_USER: "app://CSP/Reference User",
  CSP_CALL_RECORDING: "app://CSP/Call Recording",
  CSP_USER_PROVISIONING_SOURCE_MANAGEMENT: "app://CSP/User Provisioning Source Management",
  CSP_CAREWIRE: "app://CSP/CareWire",
  CSP_ROSTER_UPLOAD: "app://CSP/Roster Upload",
} as const;

// Combined permissions object for easy access
export const PERMISSIONS = {
  ...ROLES,
  ...OBJECTS,
  ...APPLICATIONS,
} as const;

// Type for all permission values
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// Helper function to get all permission values as an array
export const getAllPermissions = (): string[] => {
  return Object.values(PERMISSIONS);
};

// Helper function to validate if a string is a valid permission
export const isValidPermission = (permission: string): permission is Permission => {
  return getAllPermissions().includes(permission);
};

// Commonly used permission groups for easier reference
export const PERMISSION_GROUPS = {
  // Permissions that grant PerfectServe employee access
  PERFECT_SERVE_EMPLOYEE_PERMISSIONS: [PERMISSIONS.PERFECT_SERVE_EMPLOYEE],

  // Permissions for Care Mobility access
  CARE_MOBILITY_PERMISSIONS: [PERMISSIONS.PERFECT_SERVE_EMPLOYEE, PERMISSIONS.HOSPITAL_PRACTITIONER_ADMINISTRATOR],

  // Permissions for Interactions access
  INTERACTIONS_PERMISSIONS: [PERMISSIONS.PERFECT_SERVE_EMPLOYEE, PERMISSIONS.CSP_AFTER_HOURS_REPORTING],
} as const;
